
import { gql } from "graphql-tag";
import { mapState } from "vuex";
import linkHandlerMixin from "~/mixins/linkClickHandlerMixin";
import { colors } from "~/colors.config";

import { documentLink, externalLink } from "~/app/prismic/prismic-fragments";
import { offersSection } from "~/slices/OffersSection/fragment";
import { offersSectionV2 } from "~/slices/OffersSectionV2/fragment";
import { nextDayDelivery } from "~/slices/NextDayDelivery/fragment";
import { memberProgram } from "~/slices/MemberProgram/fragment";
import { membersProgramV2 } from "~/slices/MemberProgramV2/fragment";
import { textOverImage } from "~/slices/TextOverImage/fragment";
import { videoBanner } from "~/slices/VideoBanner/fragment";

const CAMPAIGN_SALE_QUERY = gql`
  query campaign_sale1($uid: String!, $lang: String = "en-in") {
    campaign_sale1(uid: $uid, lang: $lang) {
      title
      bg_image
      bg_color
      sale_link {
        ...documentLink
        ...externalLink
      }
      button_label
      button_label_color
      button_color
      button_link {
        ...documentLink
      }
      show_on_membership_page_only
      slices: body {
        ...offersSection
        ...offersSectionV2
        ...nextDayDelivery
        ...memberProgram
        ...textOverImage
        ...videoBanner
        ...membersProgramV2
      }
    }
  }
  ${documentLink}
  ${externalLink}
  ${offersSection}
  ${offersSectionV2}
  ${nextDayDelivery}
  ${memberProgram}
  ${textOverImage}
  ${videoBanner}
  ${membersProgramV2}
`;

export default {
  mixins: [linkHandlerMixin],

  props: {
    uid: { type: String, required: true },
    index: { type: Number, default: 0 },
  },

  data() {
    return {
      campaignSale: null,
      slices: [],
    };
  },

  async fetch() {
    const { campaign_sale1: campaignSale } = await this.$prismicGql.request({
      query: CAMPAIGN_SALE_QUERY,
      variables: { uid: this.uid },
    });

    if (campaignSale) {
      const { slices, ...rest } = campaignSale;
      this.slices = (slices ?? []).length > 0 ? [slices[0]] : [];
      this.campaignSale = rest;
    }
  },

  fetchOnServer() {
    return this.index === 0;
  },

  computed: {
    ...mapState("app", ["windowWidth"]),

    ctaLink() {
      const link = this.campaignSale?.sale_link;
      return link ? this.$prismic.asLink(link) : null;
    },

    bgImage() {
      const sm = this.campaignSale?.bg_image?.sm;
      const tab = this.campaignSale?.bg_image?.tablet;

      if (this.isMobile && sm) return sm;
      if (this.isTablet && tab) return tab;
      return this.campaignSale?.bg_image;
    },

    bgColor() {
      return this.campaignSale?.bg_color || colors.campaign.defaultBg;
    },

    showOnMembershipPageOnly() {
      return this.campaignSale?.show_on_membership_page_only ?? false;
    },

    calculateBannerHeight() {
      if (!this.bgImage?.dimensions) return {};
      const height =
        (this.bgImage.dimensions.height / this.bgImage.dimensions.width) *
        this.windowWidth;
      return { height: `${height}px` };
    },

    styles() {
      // used ONLY when showOnMembershipPageOnly === true
      if (!this.bgImage?.url) {
        return { backgroundColor: this.bgColor };
      }

      return {
        ...this.calculateBannerHeight,
        backgroundImage: `url('${this.$img(this.bgImage.url)}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    },

    outerBgStyle() {
      // used when showOnMembershipPageOnly === false
      if (this.bgImage?.url) {
        return {
          backgroundImage: `url('${this.$img(this.bgImage.url)}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
      }

      return { backgroundColor: this.bgColor };
    },
  },
};
